import { useNavigate } from "react-router-dom";
import { Controller, useForm } from 'react-hook-form';
import { useEffect, useRef, useState } from "react";
import Api from "../Auth/Api";
import { Toast, Card, Modal } from "react-bootstrap";
import Select from 'react-select';
import { customStyles } from '../utils/selectStyles';
import { decodeJWT } from '../Components/jwtUtils';

// Função para formatar CPF (XXX.XXX.XXX-XX)
const formatCPF = (value) => {
    if (!value) return '';
    const cpf = value.replace(/\D/g, '');
    if (cpf.length <= 3) return cpf;
    if (cpf.length <= 6) return `${cpf.slice(0, 3)}.${cpf.slice(3)}`;
    if (cpf.length <= 9) return `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6)}`;
    return `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-${cpf.slice(9, 11)}`;
};

function Configuracoes() {
    const navigate = useNavigate();
    const token = JSON.parse(localStorage.getItem("user_token"));
    const decodedToken = decodeJWT(token);
    const nivelUsuario = decodedToken?.nivel;
    const isAluno = nivelUsuario === 'Aluno';

    const [toast, setToast] = useState({});
    const [showToast, setShowToast] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const fileInputRef = useRef(null);
    const [userPhoto, setUserPhoto] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [gestorResponsavel, setGestorResponsavel] = useState(null);
    const [loadingGestor, setLoadingGestor] = useState(false);
    const [showModalSenha, setShowModalSenha] = useState(false);
    const [isLoadingSenha, setIsLoadingSenha] = useState(false);

    const { register, handleSubmit, setValue, control, watch, getValues, formState: { errors } } = useForm();
    const { register: registerSenha, handleSubmit: handleSubmitSenha, reset: resetSenha, formState: { errors: errorsSenha } } = useForm();

    const onSubmit = async (data) => {
        setIsLoading(true);
        const formData = new FormData();

        // Adiciona todos os campos do formulário ao formData
        Object.keys(data).forEach(key => {
            const value = data[key];
            if (typeof value === 'object' && value !== null && !(value instanceof FileList)) {
                formData.append(key, JSON.stringify(value));
            } else if (value instanceof FileList) {
                Array.from(value).forEach(file => {
                    formData.append(key, file);
                });
            } else {
                // Para CPF, garantir que apenas números sejam enviados
                if (key === 'cpf' && value) {
                    formData.append(key, value.replace(/\D/g, ''));
                } else {
                    formData.append(key, value);
                }
            }
        });

        // Adiciona a imagem, se selecionada
        if (selectedImage) {
            formData.append('foto_perfil', selectedImage);
        }

        try {
            const response = await Api.post(`api/editar-meu-perfil`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            setToast({ header: 'Sucesso!', body: response.data.message, type: 'toast-success' });
            localStorage.setItem("nome_completo", data.nome_completo)
        } catch (error) {
            setToast({ header: 'Erro!', body: error.response?.data?.message || 'Erro ao atualizar perfil' });
        } finally {
            setIsLoading(false);
        }
        setShowToast(true);
    };

    // Carregar unidades
    const [dataUnidades, setDataUnidades] = useState([]);
    useEffect(() => {
        async function fetchData() {
            try {
                const response = await Api.get(`api/listar-unidades`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setDataUnidades(response.data);
            } catch (error) {
                // Erro silencioso
            }
        }
        fetchData();
    }, [token]);

    // Carregar informações do usuario via api
    useEffect(() => {
        async function fetchData() {
            try {
                const response = await Api.get(`api/meu-perfil`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                const userData = response.data;
                
                setValue('nome_completo', userData.nome_completo);
                setValue('unidade', userData.unidade);
                setValue('email', userData.email);
                // Formatar CPF se existir
                if (userData.cpf) {
                    setValue('cpf', userData.cpf.replace(/\D/g, '')); // Salva apenas números
                } else {
                    setValue('cpf', '');
                }
                
                localStorage.setItem("foto", userData.foto || '');
                setUserPhoto(userData.foto);
                
                const selectUnidade = dataUnidades.find(item => item.nome === userData.unidade);
                if (selectUnidade && selectUnidade.nome) {
                    setValue('unidade', { value: selectUnidade.nome, label: selectUnidade.nome });
                } else {
                    setValue('unidade', { value: '', label: '' });
                }

                // Se for aluno e tiver gestor_responsavel, buscar dados do gestor
                if (isAluno && userData.gestor_responsavel) {
                    setLoadingGestor(true);
                    try {
                        const gestorResponse = await Api.get(`api/usuario/${userData.gestor_responsavel}`, {
                            headers: {
                                Authorization: `Bearer ${token}`
                            }
                        });
                        setGestorResponsavel(gestorResponse.data);
                    } catch (error) {
                        console.error('Erro ao buscar gestor:', error);
                    } finally {
                        setLoadingGestor(false);
                    }
                }
            } catch (error) {
                console.error(error);
            }
        }
        fetchData();
    }, [token, setValue, dataUnidades, isAluno]);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const imageUrl = URL.createObjectURL(file);
            setSelectedImage(file);
            setUserPhoto(imageUrl);
        }
    };

    const handleCPFChange = (e, onChange) => {
        const inputValue = e.target.value;
        const formatted = formatCPF(inputValue);
        const cpfOnlyNumbers = formatted.replace(/\D/g, '');
        setValue('cpf', cpfOnlyNumbers, { shouldValidate: false });
        if (onChange) {
            onChange({ target: { value: formatted } });
        }
    };

    const onSubmitSenha = async (data) => {
        if (!data.senhaAtual || !data.novaSenha) {
            setToast({ header: 'Erro!', body: 'Preencha todos os campos para alterar a senha' });
            setShowToast(true);
            return;
        }

        setIsLoadingSenha(true);
        const formData = new FormData();
        formData.append('senhaAtual', data.senhaAtual);
        formData.append('novaSenha', data.novaSenha);

        // A API exige nome_completo, unidade e email mesmo só trocando senha (multipart parse igual ao salvar perfil).
        const perfil = getValues();
        formData.append('nome_completo', perfil.nome_completo ?? '');
        formData.append('email', perfil.email ?? '');
        const unidadeVal = perfil.unidade;
        if (unidadeVal && typeof unidadeVal === 'object' && unidadeVal.value !== undefined) {
            formData.append('unidade', JSON.stringify(unidadeVal));
        } else if (typeof unidadeVal === 'string' && unidadeVal) {
            formData.append('unidade', JSON.stringify({ value: unidadeVal, label: unidadeVal }));
        } else {
            formData.append('unidade', JSON.stringify({ value: '', label: '' }));
        }
        if (perfil.cpf) {
            formData.append('cpf', String(perfil.cpf).replace(/\D/g, ''));
        }

        try {
            const response = await Api.post(`api/editar-meu-perfil`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });
            setToast({ header: 'Sucesso!', body: response.data.message, type: 'toast-success' });
            resetSenha();
            setShowModalSenha(false);
        } catch (error) {
            setToast({ header: 'Erro!', body: error.response?.data?.message || 'Erro ao alterar senha' });
        } finally {
            setIsLoadingSenha(false);
        }
        setShowToast(true);
    };

    return (
        <>
            <Toast
                show={showToast}
                onClose={() => setShowToast(false)}
                delay={4000}
                autohide
                className={`position-fixed top-0 end-0 m-3 toast-icon ${toast.type || 'toast-error'}`}
                style={{ zIndex: 1060 }}
            >
                <Toast.Header>
                    <strong className="me-auto">{toast.header}</strong>
                </Toast.Header>
                <Toast.Body>{toast.body}</Toast.Body>
            </Toast>

            <div className="fade-in w-100 mb-5">
                {/* Botão de Voltar Moderno */}
                <div className="d-flex justify-content-start align-items-center mb-4">
                    <button 
                        onClick={() => navigate(-1)} 
                        className="btn btn-outline-primary rounded-pill px-4 py-1 d-flex align-items-center gap-2 shadow-sm"
                        style={{
                            transition: 'all 0.3s ease',
                            borderWidth: '2px',
                            fontWeight: '500'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateX(-5px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(13, 110, 253, 0.3)';
                            e.currentTarget.style.backgroundColor = '#0d6efd';
                            e.currentTarget.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateX(0)';
                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '#0d6efd';
                        }}
                    >
                        <i className="bi bi-arrow-left fs-5"></i>
                        <span>Voltar</span>
                    </button>
                </div>

                <div className="container-fluid px-0">
                    <div className="row g-4">
                        {/* Card de Foto e Informações Básicas */}
                        <div className="col-12 col-lg-4">
                            <Card className="h-100 shadow-sm border-0">
                                <Card.Body className="text-center p-4">
                                    <input
                                        type="file"
                                        style={{ display: "none" }}
                                        ref={fileInputRef}
                                        onChange={handleImageChange}
                                        accept="image/*"
                                    />
                                    <div className="position-relative d-inline-block mb-3">
                                        {userPhoto ? (
                                            <img
                                                src={userPhoto.includes('blob') ? userPhoto : `${process.env.REACT_APP_API_URL}/fotos/${userPhoto}`}
                                                alt="Foto do usuário"
                                                className="cursor-pointer rounded-circle"
                                                style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                                                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                            />
                                        ) : (
                                            <i
                                                className="bi bi-person-circle d-block text-primary cursor-pointer"
                                                style={{ fontSize: '150px' }}
                                                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                            ></i>
                                        )}
                                        <button
                                            type="button"
                                            className="btn btn-primary rounded-circle position-absolute"
                                            style={{ bottom: '10px', right: '10px', width: '40px', height: '40px', padding: 0 }}
                                            onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                        >
                                            <i className="bi bi-camera-fill"></i>
                                        </button>
                                    </div>
                                    <h5 className="text-primary mb-1">{watch('nome_completo') || 'Nome'}</h5>
                                    <p className="text-muted small mb-0">{watch('email') || 'Email'}</p>
                                    <button
                                        type="button"
                                        className="btn btn-outline-warning mt-3"
                                        onClick={() => setShowModalSenha(true)}
                                    >
                                        <i className="bi bi-shield-lock-fill me-2"></i>
                                        Alterar Senha
                                    </button>
                                </Card.Body>
                            </Card>
                        </div>

                        {/* Card de Dados Pessoais */}
                        <div className="col-12 col-lg-8">
                            <Card className="h-100 shadow-sm border-0">
                                <Card.Header className="bg-primary text-white">
                                    <h5 className="mb-0">
                                        <i className="bi bi-person-fill me-2"></i>
                                        Dados Pessoais
                                    </h5>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <form onSubmit={handleSubmit(onSubmit)}>
                                        <div className="row g-3">
                                            <div className="col-12">
                                                <label className="form-label text-primary fw-medium">Nome Completo</label>
                                                <input 
                                                    {...register('nome_completo', { required: true })} 
                                                    type="text" 
                                                    className="form-control" 
                                                    placeholder="Digite seu nome completo"
                                                />
                                                {errors.nome_completo && (
                                                    <span className="text-danger small">Nome é obrigatório</span>
                                                )}
                                            </div>

                                            <div className="col-12 col-md-6">
                                                <label className="form-label text-primary fw-medium">CPF</label>
                                                <Controller
                                                    name="cpf"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input 
                                                            type="text" 
                                                            className="form-control" 
                                                            placeholder="000.000.000-00"
                                                            maxLength={14}
                                                            onChange={(e) => {
                                                                handleCPFChange(e, field.onChange);
                                                            }}
                                                            value={field.value ? formatCPF(field.value) : ''}
                                                        />
                                                    )}
                                                />
                                            </div>

                                            <div className="col-12 col-md-6">
                                                <label className="form-label text-primary fw-medium">Email</label>
                                                <input 
                                                    {...register('email', { required: true })} 
                                                    type="email" 
                                                    className="form-control" 
                                                    placeholder="seu@email.com"
                                                />
                                                {errors.email && (
                                                    <span className="text-danger small">Email é obrigatório</span>
                                                )}
                                            </div>

                                            <div className="col-12">
                                                <label className="form-label text-primary fw-medium">Unidade</label>
                                                <Controller
                                                    name="unidade"
                                                    control={control}
                                                    defaultValue={[]}
                                                    rules={{ required: true }}
                                                    render={({ field }) => (
                                                        <Select
                                                            {...field}
                                                            options={dataUnidades.map((value) => ({
                                                                value: value.nome,
                                                                label: value.nome,
                                                            }))}
                                                            value={field.value}
                                                            styles={customStyles}
                                                            onChange={value => field.onChange(value)}
                                                            placeholder="Selecione uma unidade"
                                                            noOptionsMessage={() => "Nenhuma unidade encontrada."}
                                                        />
                                                    )}
                                                />
                                                {errors.unidade && (
                                                    <span className="text-danger small">Selecione uma unidade</span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="mt-4 pt-3 border-top">
                                            <button 
                                                type="submit" 
                                                className="btn btn-primary px-4" 
                                                disabled={isLoading}
                                            >
                                                {isLoading ? (
                                                    <>
                                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        Salvando...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="bi bi-check-circle me-2"></i>
                                                        Salvar Alterações
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                </Card.Body>
                            </Card>
                        </div>

                        {/* Card de Gestor Responsável (apenas para Alunos) */}
                        {isAluno && (
                            <div className="col-12">
                                <Card className="h-100 shadow-sm border-0">
                                    <Card.Header className="bg-primary text-white">
                                        <h5 className="mb-0">
                                            <i className="bi bi-person-badge-fill me-2"></i>
                                            Gestor Responsável
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        {loadingGestor ? (
                                            <div className="text-center py-3">
                                                <span className="spinner-border spinner-border-sm text-primary" role="status"></span>
                                                <p className="text-muted small mt-2 mb-0">Carregando informações...</p>
                                            </div>
                                        ) : gestorResponsavel ? (
                                            <div>
                                                <div className="d-flex align-items-center mb-3">
                                                    {gestorResponsavel.foto ? (
                                                        <img
                                                            src={`${process.env.REACT_APP_API_URL}/fotos/${gestorResponsavel.foto}`}
                                                            alt={gestorResponsavel.nome_completo}
                                                            className="rounded-circle me-3"
                                                            style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                                                        />
                                                    ) : (
                                                        <i className="bi bi-person-circle text-primary me-3" style={{ fontSize: '60px' }}></i>
                                                    )}
                                                    <div>
                                                        <h6 className="mb-1 text-primary">{gestorResponsavel.nome_completo}</h6>
                                                        <p className="text-muted small mb-0">{gestorResponsavel.email}</p>
                                                        {gestorResponsavel.cargo && (
                                                            <span className="badge bg-secondary small">{gestorResponsavel.cargo}</span>
                                                        )}
                                                    </div>
                                                </div>
                                                {gestorResponsavel.unidade && (
                                                    <div className="mt-3 pt-3 border-top">
                                                        <p className="text-muted small mb-1">
                                                            <i className="bi bi-building me-2"></i>
                                                            <strong>Unidade:</strong> {gestorResponsavel.unidade}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="text-center py-3">
                                                <i className="bi bi-info-circle text-muted" style={{ fontSize: '3rem' }}></i>
                                                <p className="text-muted mt-2 mb-0">Nenhum gestor responsável atribuído</p>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Modal de Alterar Senha */}
            <Modal show={showModalSenha} onHide={() => setShowModalSenha(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="text-primary">
                        <i className="bi bi-shield-lock-fill me-2"></i>
                        Alterar Senha
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form onSubmit={handleSubmitSenha(onSubmitSenha)}>
                        <div className="mb-3">
                            <label className="form-label text-primary fw-medium">Senha Atual</label>
                            <input 
                                {...registerSenha('senhaAtual', { required: true })} 
                                type="password" 
                                className="form-control" 
                                placeholder="Digite sua senha atual"
                            />
                            {errorsSenha.senhaAtual && (
                                <span className="text-danger small">Senha atual é obrigatória</span>
                            )}
                        </div>
                        <div className="mb-3">
                            <label className="form-label text-primary fw-medium">Nova Senha</label>
                            <input 
                                {...registerSenha('novaSenha', { required: true, minLength: 6 })} 
                                type="password" 
                                className="form-control" 
                                placeholder="Digite a nova senha (mínimo 6 caracteres)"
                            />
                            {errorsSenha.novaSenha && (
                                <span className="text-danger small">
                                    {errorsSenha.novaSenha.type === 'required' 
                                        ? 'Nova senha é obrigatória' 
                                        : 'A senha deve ter no mínimo 6 caracteres'}
                                </span>
                            )}
                        </div>
                        <div className="d-flex justify-content-end gap-2 mt-4">
                            <button 
                                type="button" 
                                className="btn" 
                                onClick={() => {
                                    setShowModalSenha(false);
                                    resetSenha();
                                }}
                                disabled={isLoadingSenha}
                            >
                                Cancelar
                            </button>
                            <button 
                                type="submit" 
                                className="btn btn-warning" 
                                disabled={isLoadingSenha}
                            >
                                {isLoadingSenha ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Alterando...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-check-circle me-2"></i>
                                        Alterar Senha
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </Modal.Body>
            </Modal>
        </>
    )
}

export default Configuracoes;
